import { useEffect, useState, useCallback } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Panel } from "primereact/panel";
import { Message } from "primereact/message";
import { Avatar } from "primereact/avatar";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { classNames } from "primereact/utils";
import { passengerAdmin, getAdminConfigWarning, asAdminList } from "../components/services/adminBackendFetch";
import { DetailRecordCard } from "../components/DetailRecordCard";
import { AdminPageHeader } from "../components/AdminPageHeader";
import { AdminTableShell } from "../components/AdminTableShell";
import { useAppContext } from "../providers/ProviderAppContainer";
import { formatAdminDateTime } from "../utils/formatAdminDateTime";

const RIDE_LIMIT_OPTIONS = [
    { label: "50", value: 50 },
    { label: "100", value: 100 },
    { label: "200", value: 200 },
    { label: "500", value: 500 },
];

function formatRideTablePrice(v) {
    if (v == null) return "—";
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

const RIDE_CORE_LABELS = {
    status: "Status",
    receipt_number: "Receipt number",
    payment_method: "Payment method",
    stripe_payment_status: "Stripe payment status",
    payment_authorized_amount: "Payment authorized",
    payment_captured_amount: "Payment captured",
    price: "Price",
    cancellation_fee: "Cancellation fee",
    cancel_type: "Cancel type",
    created_at: "Created",
    start_time_stamp: "Trip started",
    end_time_stamp: "Trip ended",
};

const RIDE_CORE_ORDER = [
    "status",
    "receipt_number",
    "price",
    "cancellation_fee",
    "cancel_type",
    "payment_method",
    "stripe_payment_status",
    "payment_authorized_amount",
    "payment_captured_amount",
    "created_at",
    "start_time_stamp",
    "end_time_stamp",
];

/** `ride_user` row — person actually travelling (shown only when the booking includes rider contact). */
const RIDER_TRIP_LABELS = {
    full_name: "Passenger name",
    phone: "Passenger Phone Details",
};

const RIDER_TRIP_ORDER = ["full_name", "phone"];

/** USERS row — logged-in account that created the booking (may be the same person or someone booking for another). */
const BOOKING_ACCOUNT_LABELS = {
    full_name: "Account holder name",
    phone: "Account phone",
    email: "Email",
    active: "Active",
    created_on: "Account created",
    updated_at: "Account updated",
};

const BOOKING_ACCOUNT_ORDER = ["full_name", "phone", "email", "active", "created_on", "updated_at"];

const DRIVER_LABELS = {
    full_name: "Full name",
    phone: "Phone",
    email: "Email",
    current_latitude: "Last latitude",
    current_longitude: "Last longitude",
};

const DRIVER_ORDER = ["full_name", "phone", "email", "current_latitude", "current_longitude"];

const ASSIGNED_VEHICLE_LABELS = {
    driver_vehicle_number: "Registration number",
    driver_vehicle_model: "Model",
    driver_vehicle_colour: "Colour",
};

const ASSIGNED_VEHICLE_ORDER = ["driver_vehicle_number", "driver_vehicle_model", "driver_vehicle_colour"];

/** Hide internal keys in `DetailRecordCard` rows for this page. */
const RIDE_DETAIL_SKIP_KEYS = [
    "id",
    "user_id",
    "driver_id",
    "vehical_id",
    "ride_id",
    "avatar_id",
    "image",
];

function hasRiderTripContact(rideUser) {
    if (!rideUser || typeof rideUser !== "object") return false;
    const name = rideUser.full_name != null && String(rideUser.full_name).trim() !== "";
    const phone = rideUser.phone != null && String(rideUser.phone).trim() !== "";
    return name || phone;
}

const statusSeverity = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "completed") return "success";
    if (s === "assigned" || s === "inprogress") return "info";
    if (s === "new") return "warning";
    return null;
};

function rideCoreFromDetail(detail) {
    if (!detail || typeof detail !== "object") return null;
    const omit = new Set([
        "vehicle",
        "stops",
        "ride_user",
        "passenger_account",
        "driver",
        "driver_vehicle_number",
        "driver_vehicle_model",
        "driver_vehicle_colour",
        "id",
        "user_id",
        "driver_id",
        "vehical_id",
    ]);
    const o = {};
    for (const k of Object.keys(detail)) {
        if (omit.has(k)) continue;
        const v = detail[k];
        if (v !== null && typeof v === "object" && !Array.isArray(v)) continue;
        o[k] = v;
    }
    return Object.keys(o).length ? o : null;
}

function assignedVehicleFromDetail(detail) {
    if (!detail || typeof detail !== "object") return null;
    const keys = ["driver_vehicle_number", "driver_vehicle_model", "driver_vehicle_colour"];
    const o = {};
    for (const k of keys) {
        const v = detail[k];
        if (v != null && String(v).trim() !== "") o[k] = v;
    }
    return Object.keys(o).length ? o : null;
}

function formatRideHeaderDate(value) {
    if (value == null || value === "") return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function pickupDropFromStops(stops) {
    if (!Array.isArray(stops) || stops.length === 0) {
        return { pickupName: null, dropName: null };
    }
    const sorted = [...stops].sort((a, b) => (a.stop_order ?? 0) - (b.stop_order ?? 0));
    return {
        pickupName: sorted[0]?.location_name?.trim() || null,
        dropName: sorted[sorted.length - 1]?.location_name?.trim() || null,
    };
}

function formatStatusLabel(status) {
    if (status == null || status === "") return null;
    const s = String(status);
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/** Table rows use `passenger_name`; full detail uses `ride_user` / `passenger_account`. */
function rideRowLabel(row) {
    if (!row || typeof row !== "object") return "this trip";
    const name =
        (row.passenger_name != null && String(row.passenger_name).trim()) ||
        (row.ride_user?.full_name != null && String(row.ride_user.full_name).trim()) ||
        (row.passenger_account?.full_name != null && String(row.passenger_account.full_name).trim()) ||
        "";
    const pickup = row.pickup_name != null && String(row.pickup_name).trim();
    const { pickupName } = pickupDropFromStops(row.stops);
    const pickupFromStops = pickupName != null && String(pickupName).trim();
    return name || pickup || pickupFromStops || `Trip #${row.id}`;
}

/** Picks only the fields we show for “vehicle type & quote” — no raw API dump. */
function RideDetailPanel({ header, iconClass, children }) {
    return (
        <Panel
            header={
                <span className="flex align-items-center gap-2">
                    {iconClass ? <i className={classNames(iconClass, "text-primary text-base")} aria-hidden /> : null}
                    <span className="font-semibold text-900 text-sm sm:text-base">{header}</span>
                </span>
            }
            className={classNames(
                "uc-admin-ride-panel",
                "shadow-1",
                "m-0",
                "border-1",
                "surface-border",
                "border-round-lg",
                "overflow-hidden",
            )}
            pt={{
                header: {
                    className: classNames(
                        "surface-50",
                        "border-bottom-1",
                        "surface-border",
                        "py-1 px-2 sm:px-3",
                    ),
                },
                content: { className: classNames("p-0") },
                title: { className: classNames("w-full") },
            }}
        >
            {children}
        </Panel>
    );
}

function vehicleQuoteRows(vehicle) {
    if (!vehicle || typeof vehicle !== "object") return [];
    const rows = [
        { label: "Vehicle type", value: vehicle.name },
        { label: "Class", value: vehicle.type },
        { label: "Capacity", value: vehicle.capacity != null ? String(vehicle.capacity) : null },
        { label: "Base fare", value: vehicle.base_fare != null ? String(vehicle.base_fare) : null },
        { label: "Trip distance", value: vehicle.distance_km != null ? `${vehicle.distance_km} km` : null },
        { label: "ETA", value: vehicle.eta_minutes != null ? `${vehicle.eta_minutes} min` : null },
        { label: "Quoted price", value: vehicle.price != null ? String(vehicle.price) : null },
    ];
    return rows.filter((r) => r.value != null && String(r.value).trim() !== "");
}

function VehicleQuoteGrid({ rows }) {
    if (!rows?.length) return null;
    return (
        <div className="surface-ground p-2 border-round-bottom-lg">
            <div className="grid gap-2">
                {rows.map((r) => (
                    <div key={r.label} className="col-12 sm:col-6 lg:col-4">
                        <div className="surface-0 border-round-lg p-2 h-full border-1 surface-border">
                            <div className="text-600 text-xs font-medium uppercase letter-spacing-1 mb-1">{r.label}</div>
                            <div
                                className="text-900 text-sm line-height-3"
                                style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                            >
                                {r.value}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function RideStopsContent({ stops }) {
    if (!Array.isArray(stops) || stops.length === 0) return null;
    const sorted = [...stops].sort((a, b) => (a.stop_order ?? 0) - (b.stop_order ?? 0));
    return (
        <div className="surface-ground p-2 flex flex-column gap-2 border-round-bottom-lg">
            {sorted.map((s, idx) => (
                <div
                    key={s.id ?? `${s.stop_order}-${idx}`}
                    className="flex gap-2 align-items-start surface-0 border-round-lg p-2 border-1 surface-border"
                >
                    <Tag value={`#${s.stop_order ?? idx}`} className="flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                        <div className="font-semibold text-900 text-sm">
                            {s.location_name || `Stop ${(s.stop_order ?? idx) + 1}`}
                        </div>
                        <div className="text-600 text-xs mt-1 line-height-3">
                            {[s.latitude, s.longitude].filter((x) => x != null && x !== "").join(", ") || "—"}
                            {s.pincode != null && s.pincode !== "" ? ` · ${s.pincode}` : ""}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function rideDialogHeader(detail) {
    if (!detail) return "Trip details";
    const riderName =
        detail.ride_user?.full_name?.trim() ||
        detail.passenger_account?.full_name?.trim() ||
        "";
    const title = riderName || "Trip";
    const { pickupName, dropName } = pickupDropFromStops(detail.stops);
    const pickupWhen = formatRideHeaderDate(detail.start_time_stamp || detail.created_at);
    const dropWhen = formatRideHeaderDate(detail.end_time_stamp);
    const dropLine =
        dropWhen ||
        (String(detail.status || "").toLowerCase() !== "completed" ? "Not completed yet" : "—");
    const dropMuted = !dropWhen && String(detail.status || "").toLowerCase() !== "completed";

    return (
        <div className="flex align-items-start gap-2 sm:gap-3 pr-4 min-w-0">
            <Avatar
                icon="pi pi-map"
                className={classNames(
                    "bg-primary",
                    "text-primary-contrast",
                    "flex-shrink-0",
                    "uc-admin-dialog-title-avatar",
                )}
                shape="circle"
            />
            <div className="min-w-0 flex-1">
                <div className="flex align-items-center gap-2 flex-wrap">
                    <div className="uc-admin-dialog-title">{title}</div>
                    {detail.status ? (
                        <Tag value={formatStatusLabel(detail.status) || detail.status} severity={statusSeverity(detail.status)} className="text-xs" />
                    ) : null}
                </div>
                <div className="uc-admin-dialog-trip-strip mt-2">
                    <div className="uc-admin-dialog-trip-cell">
                        <div className="uc-admin-dialog-trip-label">
                            <i className="pi pi-sign-in" aria-hidden />
                            Pickup
                        </div>
                        <div className="uc-admin-dialog-trip-place">{pickupName || "—"}</div>
                        <div className="uc-admin-dialog-trip-time">{pickupWhen || "—"}</div>
                    </div>
                    <div className="uc-admin-dialog-trip-cell">
                        <div className="uc-admin-dialog-trip-label">
                            <i className="pi pi-sign-out" aria-hidden />
                            Drop-off
                        </div>
                        <div className="uc-admin-dialog-trip-place">{dropName || "—"}</div>
                        <div
                            className={classNames(
                                "uc-admin-dialog-trip-time",
                                dropMuted && "uc-admin-dialog-trip-time--muted",
                            )}
                        >
                            {dropLine}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function RidesPage() {
    const { showToast } = useAppContext();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rideLimit, setRideLimit] = useState(200);
    const [detail, setDetail] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);

    const loadRides = useCallback(() => {
        const warn = getAdminConfigWarning();
        if (warn) {
            showToast({ severity: "warn", summary: "Configuration", detail: warn });
            setLoading(false);
            return Promise.resolve();
        }
        setLoading(true);
        return passengerAdmin(`/admin/rides?limit=${rideLimit}`)
            .then((data) => {
                const list = asAdminList(data);
                if (!Array.isArray(data)) {
                    showToast({
                        severity: "warn",
                        summary: "Unexpected response",
                        detail: "The rides list was not in the expected format.",
                    });
                }
                setRows(list);
            })
            .catch((e) => {
                showToast({
                    severity: "error",
                    summary: "Could not load rides",
                    detail: String(e.message || e),
                });
                setRows([]);
            })
            .finally(() => setLoading(false));
    }, [rideLimit, showToast]);

    useEffect(() => {
        loadRides();
    }, [loadRides]);

    async function openDetail(row) {
        setDetailOpen(true);
        setDetail(null);
        setDetailLoading(true);
        try {
            const d = await passengerAdmin(`/admin/rides/${row.id}/full`);
            setDetail(d);
        } catch (e) {
            showToast({
                severity: "error",
                summary: "Ride detail",
                detail: String(e.message || e),
            });
            setDetailOpen(false);
        } finally {
            setDetailLoading(false);
        }
    }

    function requestDeleteRide(row) {
        const label = rideRowLabel(row);
        confirmDialog({
            message: `Permanently delete ${label}? Stops and booking data for this trip will be removed. This cannot be undone. After this, you can delete the driver or passenger if they have no other rides.`,
            header: "Delete trip",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            acceptLabel: "Delete",
            rejectLabel: "Cancel",
            accept: async () => {
                try {
                    await passengerAdmin(`/admin/rides/${row.id}`, { method: "DELETE" });
                    showToast({ severity: "success", summary: "Trip deleted", detail: `Ride #${row.id}` });
                    if (detailOpen && detail?.id === row.id) {
                        setDetailOpen(false);
                        setDetail(null);
                    }
                    await loadRides();
                } catch (e) {
                    showToast({
                        severity: "error",
                        summary: "Delete failed",
                        detail: String(e.message || e),
                    });
                }
            },
        });
    }

    const actionsBody = (row) => (
        <div className="uc-admin-table-actions">
            <Button label="View" icon="pi pi-eye" size="small" outlined onClick={() => openDetail(row)} />
            <Button
                label="Delete"
                icon="pi pi-trash"
                size="small"
                severity="danger"
                outlined
                onClick={() => requestDeleteRide(row)}
            />
        </div>
    );

    const detailDialogFooter =
        detail && !detailLoading ? (
            <div className="flex justify-content-between align-items-center flex-wrap gap-2 w-full">
                <Button
                    label="Delete trip"
                    icon="pi pi-trash"
                    severity="danger"
                    outlined
                    onClick={() => requestDeleteRide(detail)}
                />
                <Button label="Close" icon="pi pi-times" className="p-button-outlined" onClick={() => setDetailOpen(false)} />
            </div>
        ) : null;

    const rideCore = detail ? rideCoreFromDetail(detail) : null;
    const assignedVehicle = detail ? assignedVehicleFromDetail(detail) : null;
    const vehicleQuoteList =
        detail?.vehicle && typeof detail.vehicle === "object" ? vehicleQuoteRows(detail.vehicle) : [];

    return (
        <div>
            <ConfirmDialog />
            <AdminPageHeader
                title="Rides"
                subtitle="Trips created from passenger bookings. Use View for full detail, or Delete to remove a trip so linked drivers can be removed when they have no remaining rides."
                meta={
                    !loading
                        ? `${rows.length} ride${rows.length === 1 ? "" : "s"} loaded (up to ${rideLimit} recent)`
                        : undefined
                }
                actions={
                    <>
                        <span className="text-600 text-sm white-space-nowrap">Rows to load</span>
                        <Dropdown
                            value={rideLimit}
                            options={RIDE_LIMIT_OPTIONS}
                            onChange={(e) => setRideLimit(e.value)}
                            className="w-10rem"
                        />
                        <Button
                            label="Refresh"
                            icon="pi pi-refresh"
                            className="p-button-outlined"
                            onClick={loadRides}
                            disabled={loading}
                        />
                    </>
                }
            />
            {loading ? (
                <div className="flex justify-content-center p-6">
                    <ProgressSpinner />
                </div>
            ) : (
                <AdminTableShell
                    header={
                        <>
                            <i className="pi pi-table text-primary" aria-hidden />
                            <span className="uc-admin-table-toolbar-title">Trip list</span>
                        </>
                    }
                >
                    <DataTable
                        value={rows}
                        paginator
                        paginatorDropdownAppendTo={typeof document !== "undefined" ? document.body : undefined}
                        rows={15}
                        rowsPerPageOptions={[15, 25, 50]}
                        stripedRows
                        showGridlines
                        emptyMessage="No rides found."
                        size="small"
                        responsiveLayout="stack"
                        breakpoint="768px"
                        className="uc-admin-datatable-fluid"
                    >
                        <Column
                            field="id"
                            header="ID"
                            sortable
                            body={(r) => <span className="uc-admin-cell-nowrap">{r.id}</span>}
                        />
                        <Column
                            field="status"
                            header="Status"
                            sortable
                            body={(r) => <Tag value={r.status} severity={statusSeverity(r.status)} />}
                        />
                        <Column field="passenger_name" header="Passenger" sortable />
                        <Column
                            field="passenger_phone"
                            header="Phone"
                            body={(r) => <span className="uc-admin-cell-nowrap">{r.passenger_phone ?? "—"}</span>}
                        />
                        <Column field="pickup_name" header="Pickup" />
                        <Column field="drop_name" header="Drop" />
                        <Column
                            field="driver_name"
                            header="Driver"
                            sortable
                            body={(r) => {
                                const n = r.driver_name != null ? String(r.driver_name).trim() : "";
                                return n || <span className="text-500">—</span>;
                            }}
                        />
                        <Column
                            field="price"
                            header="Price"
                            body={(r) => (
                                <span className="uc-admin-cell-nowrap">{formatRideTablePrice(r.price)}</span>
                            )}
                        />
                        <Column
                            field="created_at"
                            header="Created"
                            sortable
                            body={(r) => (
                                <span className="uc-admin-cell-nowrap">{formatAdminDateTime(r.created_at)}</span>
                            )}
                        />
                        <Column header="Actions" body={actionsBody} />
                    </DataTable>
                </AdminTableShell>
            )}

            <Dialog
                header={rideDialogHeader(detail)}
                visible={detailOpen}
                style={{ width: "min(52rem, 96vw)" }}
                onHide={() => setDetailOpen(false)}
                footer={detailDialogFooter}
                draggable={false}
                dismissableMask
                className="uc-admin-dialog"
                pt={{
                    content: { className: classNames("uc-admin-dialog-content", "border-round-bottom-xl") },
                }}
            >
                {detailLoading ? (
                    <div className="flex justify-content-center p-5">
                        <ProgressSpinner />
                    </div>
                ) : detail ? (
                    <div className="flex flex-column gap-2">
                        <Message
                            severity="info"
                            text="Each section below appears only when data exists for this booking."
                            className={classNames(
                                "w-full",
                                "border-round-lg",
                                "uc-admin-ride-detail-message",
                                "uc-admin-dialog-banner",
                            )}
                        />

                        <RideDetailPanel header="Trip status" iconClass="pi pi-flag">
                            <div className="surface-ground p-2 flex flex-wrap align-items-center gap-2 border-round-bottom-lg">
                                <span className="text-600 text-xs font-semibold">Current status</span>
                                <Tag value={detail.status} severity={statusSeverity(detail.status)} />
                            </div>
                        </RideDetailPanel>

                        {hasRiderTripContact(detail.ride_user) ? (
                            <RideDetailPanel header="Passenger Details" iconClass="pi pi-user">
                                <DetailRecordCard
                                    data={detail.ride_user}
                                    labelMap={RIDER_TRIP_LABELS}
                                    preferredOrder={RIDER_TRIP_ORDER}
                                    skipKeys={RIDE_DETAIL_SKIP_KEYS}
                                />
                            </RideDetailPanel>
                        ) : detail.passenger_account && typeof detail.passenger_account === "object" ? (
                            <RideDetailPanel header="Passenger — taking this trip" iconClass="pi pi-user">
                                <div className="surface-ground p-2 text-600 text-xs line-height-3 border-round-bottom-lg">
                                    <span className="font-semibold text-900">No passenger contact on file</span>
                                    <span className="block mt-2">
                                        No passenger name or phone was stored on this booking. If someone booked for another
                                        passenger, that contact may appear once the passenger app saves trip details.
                                    </span>
                                </div>
                            </RideDetailPanel>
                        ) : null}

                        {detail.passenger_account && typeof detail.passenger_account === "object" ? (
                            <RideDetailPanel header="Booking Account Details" iconClass="pi pi-id-card">
                                <DetailRecordCard
                                    data={detail.passenger_account}
                                    labelMap={BOOKING_ACCOUNT_LABELS}
                                    preferredOrder={BOOKING_ACCOUNT_ORDER}
                                    skipKeys={RIDE_DETAIL_SKIP_KEYS}
                                />
                            </RideDetailPanel>
                        ) : detail.user_id ? (
                            <RideDetailPanel header="Booking account" iconClass="pi pi-id-card">
                                <div className="surface-ground p-2 text-600 text-xs border-round-bottom-lg">
                                    No app account profile found for this booking.
                                </div>
                            </RideDetailPanel>
                        ) : null}

                        {rideCore ? (
                            <RideDetailPanel header="Ride details" iconClass="pi pi-info-circle">
                                <DetailRecordCard
                                    data={rideCore}
                                    labelMap={RIDE_CORE_LABELS}
                                    preferredOrder={RIDE_CORE_ORDER}
                                    skipKeys={RIDE_DETAIL_SKIP_KEYS}
                                />
                            </RideDetailPanel>
                        ) : null}

                        {detail.driver_id ? (
                            <RideDetailPanel header="Driver details" iconClass="pi pi-user-edit">
                                {detail.driver && typeof detail.driver === "object" ? (
                                    <DetailRecordCard
                                        data={detail.driver}
                                        labelMap={DRIVER_LABELS}
                                        preferredOrder={DRIVER_ORDER}
                                        skipKeys={RIDE_DETAIL_SKIP_KEYS}
                                    />
                                ) : (
                                    <div className="surface-ground p-2 text-600 text-xs border-round-bottom-lg">
                                        Driver profile could not be loaded. Check that the driver service is linked on
                                        the server.
                                    </div>
                                )}
                            </RideDetailPanel>
                        ) : (
                            <RideDetailPanel header="Driver details" iconClass="pi pi-user-edit">
                                <div className="surface-ground p-2 text-600 text-xs border-round-bottom-lg">
                                    No driver assigned to this ride yet.
                                </div>
                            </RideDetailPanel>
                        )}

                        {vehicleQuoteList.length > 0 ? (
                            <RideDetailPanel header="Vehicle type & fare quote" iconClass="pi pi-car">
                                <VehicleQuoteGrid rows={vehicleQuoteList} />
                            </RideDetailPanel>
                        ) : null}

                        {assignedVehicle ? (
                            <RideDetailPanel header="Assigned vehicle (on ride)" iconClass="pi pi-tag">
                                <DetailRecordCard
                                    data={assignedVehicle}
                                    labelMap={ASSIGNED_VEHICLE_LABELS}
                                    preferredOrder={ASSIGNED_VEHICLE_ORDER}
                                    skipKeys={RIDE_DETAIL_SKIP_KEYS}
                                />
                            </RideDetailPanel>
                        ) : null}

                        {Array.isArray(detail.stops) && detail.stops.length > 0 ? (
                            <RideDetailPanel header="Route details" iconClass="pi pi-map-marker">
                                <RideStopsContent stops={detail.stops} />
                            </RideDetailPanel>
                        ) : null}
                    </div>
                ) : null}
            </Dialog>
        </div>
    );
}
